import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import { ReactComponent as ArrowUp } from "../../assets/icons/svg/arrow-up.svg";
import { ReactComponent as ArrowDown } from "../../assets/icons/svg/arrow-down.svg";
import { useScreens } from "../../context/ScreenContext"; // Import screens context
import { SelectedTag } from "../../context/TagContext"; // Import SelectedTag type
import AnnouncementTypeCard from "../ui/AnnouncementTypeCard"; // Import the reusable card component

type AnnouncementType = "general" | "tag-level";

interface AnnouncementSelectorProps {
  onSelectionChange: (
    type: AnnouncementType | null,
    isComplete?: boolean,
    details?: any // Add details parameter
  ) => void;
  selectedType: AnnouncementType | null;
}

interface Screen {
  id: string;
  name: string;
  group: string;
  orientation: string;
  tags: SelectedTag[];
  status: string;
  createdAt: Date;
}

interface MultiSelectDropdownProps {
  options: { id: string; label: string }[];
  selectedOptions: string[];
  onSelectionChange: (selected: string[]) => void;
  placeholder: string;
  disabled?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedOptions,
  onSelectionChange,
  placeholder,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOptionToggle = (optionId: string) => {
    const newSelection = selectedOptions.includes(optionId)
      ? selectedOptions.filter((id) => id !== optionId)
      : [...selectedOptions, optionId];
    onSelectionChange(newSelection);
  };

  const handleRemoveOption = (optionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelection = selectedOptions.filter((id) => id !== optionId);
    onSelectionChange(newSelection);
  };

  const getDisplayText = () => {
    if (selectedOptions.length === 0) return placeholder;
    if (selectedOptions.length === 1) {
      const option = options.find((opt) => opt.id === selectedOptions[0]);
      return option?.label || placeholder;
    }
    return `${selectedOptions.length} ${
      selectedOptions.length === 1 ? "tag" : "tags"
    } selected`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full h-12 px-4 py-2 text-left
          border-[0.5px] border-[#D4D4D4] rounded-[8px]
          flex items-center justify-between
          transition-colors duration-200
          ${
            disabled
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white hover:border-gray-400 cursor-pointer"
          }
          ${isOpen ? "border-blue-500 ring-1 ring-blue-100" : ""}
        `}
      >
        <span
          className={`text-sm ${
            selectedOptions.length === 0 ? "text-gray-500" : "text-gray-900"
          }`}
        >
          {getDisplayText()}
        </span>
        <span>{isOpen ? <ArrowUp /> : <ArrowDown />}</span>
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 px-[10px] py-[12px] gap-[10px] bg-white border border-gray-200 rounded-[10px] shadow-lg max-h-60 overflow-y-auto">
          {options.map((option) => (
            <div
              key={option.id}
              onClick={() => handleOptionToggle(option.id)}
              className={`
                px-4 py-3 cursor-pointer transition-colors duration-150
                flex items-center gap-3 rounded-[6px] mb-1
                ${
                  selectedOptions.includes(option.id)
                    ? "bg-[#EFFCFF] text-black"
                    : "hover:bg-[#EFFCFF] text-black"
                }
              `}
            >
              <div
                className={`w-4 h-4 border-[1px] rounded-[2px] flex items-center justify-center ${
                  selectedOptions.includes(option.id)
                    ? "bg-[#0091AF] border-[#0091AF]"
                    : "border-gray-300"
                }`}
              >
                {selectedOptions.includes(option.id) && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <span className="text-sm font-medium">{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AnnouncementSelector: React.FC<AnnouncementSelectorProps> = ({
  onSelectionChange,
  selectedType,
}) => {
  const { screens } = useScreens(); // Get screens from context
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  // Extract unique tags and values from screens
  const { tagsOptions, valuesOptions } = React.useMemo(() => {
    const tagMap = new Map<string, Set<string>>();
    const allValues = new Set<string>();

    // Extract all tags and their values from screens
    screens.forEach((screen: Screen) => {
      if (screen.tags && screen.tags.length > 0) {
        screen.tags.forEach((tag: SelectedTag) => {
          const tagName = tag.tag.toLowerCase();

          if (!tagMap.has(tagName)) {
            tagMap.set(tagName, new Set());
          }

          tagMap.get(tagName)?.add(tag.value);
          allValues.add(tag.value);
        });
      }
    });

    // Convert to options format
    const tags = Array.from(tagMap.keys()).map((tagName) => ({
      id: tagName,
      label: tagName.charAt(0).toUpperCase() + tagName.slice(1), // Capitalize first letter
    }));

    const values = Array.from(allValues).map((value) => ({
      id: value.toLowerCase(),
      label: value,
    }));

    return {
      tagsOptions: tags.sort((a, b) => a.label.localeCompare(b.label)),
      valuesOptions: values.sort((a, b) => a.label.localeCompare(b.label)),
    };
  }, [screens]);

  // Get available values based on selected tags
  const availableValuesForSelectedTags = React.useMemo(() => {
    if (selectedTags.length === 0) {
      return valuesOptions;
    }

    const availableValues = new Set<string>();

    screens.forEach((screen: Screen) => {
      if (screen.tags && screen.tags.length > 0) {
        const screenTagNames = screen.tags.map((tag) => tag.tag.toLowerCase());
        const hasSelectedTag = selectedTags.some((selectedTag) =>
          screenTagNames.includes(selectedTag)
        );

        if (hasSelectedTag) {
          screen.tags.forEach((tag) => {
            if (selectedTags.includes(tag.tag.toLowerCase())) {
              availableValues.add(tag.value);
            }
          });
        }
      }
    });

    return Array.from(availableValues)
      .map((value) => ({
        id: value.toLowerCase(),
        label: value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedTags, screens, valuesOptions]);

  const handleAnnouncementSelect = (type: AnnouncementType) => {
    const newSelection = type === selectedType ? null : type;

    // Reset dropdowns when switching announcement types
    if (newSelection !== selectedType) {
      setSelectedTags([]);
      setSelectedValues([]);
    }

    // For general announcement, it's immediately complete
    // For tag level, check if both dropdowns have selections
    const isComplete =
      newSelection === "general" ||
      (newSelection === "tag-level" &&
        selectedTags.length > 0 &&
        selectedValues.length > 0);

    // Prepare details object
    const details =
      newSelection === "tag-level"
        ? {
            selectedTags,
            selectedValues,
            availableValues: availableValuesForSelectedTags,
            tagsOptions,
            valuesOptions,
          }
        : null;

    onSelectionChange(newSelection, isComplete, details);
  };

  const handleTagsChange = (tags: string[]) => {
    setSelectedTags(tags);
    // Reset values when tags change
    setSelectedValues([]);

    // Check if form is complete
    const isComplete =
      selectedType === "tag-level" &&
      tags.length > 0 &&
      selectedValues.length > 0;

    // Prepare details object
    const details =
      selectedType === "tag-level"
        ? {
            selectedTags: tags,
            selectedValues: [], // Reset values
            availableValues: availableValuesForSelectedTags,
            tagsOptions,
            valuesOptions,
          }
        : null;

    onSelectionChange(selectedType, isComplete, details);
  };

  const handleValuesChange = (values: string[]) => {
    setSelectedValues(values);

    // Check if form is complete
    const isComplete =
      selectedType === "tag-level" &&
      selectedTags.length > 0 &&
      values.length > 0;

    // Prepare details object
    const details =
      selectedType === "tag-level"
        ? {
            selectedTags,
            selectedValues: values,
            availableValues: availableValuesForSelectedTags,
            tagsOptions,
            valuesOptions,
          }
        : null;

    onSelectionChange(selectedType, isComplete, details);
  };

  const handleRemoveSelectedTag = (tagId: string) => {
    const newTags = selectedTags.filter((id) => id !== tagId);
    handleTagsChange(newTags);
  };

  const handleRemoveSelectedValue = (valueId: string) => {
    const newValues = selectedValues.filter((id) => id !== valueId);
    handleValuesChange(newValues);
  };

  const clearAllSelections = () => {
    setSelectedTags([]);
    setSelectedValues([]);

    // Prepare details object for cleared state
    const details =
      selectedType === "tag-level"
        ? {
            selectedTags: [],
            selectedValues: [],
            availableValues: availableValuesForSelectedTags,
            tagsOptions,
            valuesOptions,
          }
        : null;

    onSelectionChange(selectedType, false, details);
  };

  // Get display labels for selected items
  const getTagLabel = (tagId: string) => {
    return tagsOptions.find((option) => option.id === tagId)?.label || tagId;
  };

  const getValueLabel = (valueId: string) => {
    return (
      availableValuesForSelectedTags.find((option) => option.id === valueId)
        ?.label || valueId
    );
  };

  return (
    <div className="flex flex-col gap-[18px]">
      <h3 className="text-lg font-semibold text-black">Announcement type:</h3>

      <div className="flex flex-col gap-4">
        <AnnouncementTypeCard
          type="general"
          isSelected={selectedType === "general"}
          onClick={() => handleAnnouncementSelect("general")}
          showDescription={true}
        />

        <AnnouncementTypeCard
          type="tag-level"
          isSelected={selectedType === "tag-level"}
          onClick={() => handleAnnouncementSelect("tag-level")}
          showDescription={true}
        />
      </div>

      {/* Show dropdowns only when Tag level announcement is selected */}
      {selectedType === "tag-level" && (
        <div className="flex flex-col gap-6 mt-4">
          {/* Tags Dropdown */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-[#323232]">
                Tags<span className="text-red-500 ml-1">*</span>
              </label>
              <span className="text-xs text-gray-500">
                ({tagsOptions.length} available)
              </span>
            </div>
            <MultiSelectDropdown
              options={tagsOptions}
              selectedOptions={selectedTags}
              onSelectionChange={handleTagsChange}
              placeholder={
                tagsOptions.length > 0 ? "Select tags" : "No tags available"
              }
              disabled={tagsOptions.length === 0}
            />
          </div>

          {/* Values Dropdown */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-[#323232]">
                Values<span className="text-red-500 ml-1">*</span>
              </label>
              <span className="text-xs text-gray-500">
                ({availableValuesForSelectedTags.length} available)
              </span>
            </div>
            <MultiSelectDropdown
              options={availableValuesForSelectedTags}
              selectedOptions={selectedValues}
              onSelectionChange={handleValuesChange}
              placeholder={
                selectedTags.length === 0
                  ? "Select tags first"
                  : availableValuesForSelectedTags.length > 0
                  ? "Select values"
                  : "No values available for selected tags"
              }
              disabled={
                selectedTags.length === 0 ||
                availableValuesForSelectedTags.length === 0
              }
            />
          </div>

          {/* Selected Items Display */}
          {(selectedTags.length > 0 || selectedValues.length > 0) && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#323232]">
                  Selected ({selectedTags.length + selectedValues.length})
                </span>
                <button
                  onClick={clearAllSelections}
                  className="text-sm text-red-500 hover:text-red-700 font-medium"
                >
                  Clear all
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {selectedTags.map((tagId) => (
                  <div
                    key={`tag-${tagId}`}
                    className="flex items-center gap-2 px-3 py-1.5 bg-[#F8F8F8] border-[0.5px] border-black rounded-full text-sm font-medium text-black"
                  >
                    <span>{getTagLabel(tagId)}</span>
                    <button
                      onClick={() => handleRemoveSelectedTag(tagId)}
                      className="hover:bg-gray-300 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                {selectedValues.map((valueId) => (
                  <div
                    key={`value-${valueId}`}
                    className="flex items-center gap-2 px-3 py-1.5 bg-[#F8F8F8] border-[0.5px] border-black rounded-full text-sm font-medium text-black"
                  >
                    <span>{getValueLabel(valueId)}</span>
                    <button
                      onClick={() => handleRemoveSelectedValue(valueId)}
                      className="hover:bg-gray-300 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Information message when no screens have tags */}
          {tagsOptions.length === 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-[8px] p-4">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-xs">ℹ</span>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-1">
                    No tags available
                  </h4>
                  <p className="text-sm text-blue-700">
                    No screens have been tagged yet. Add tags to screens first
                    to use tag-level announcements.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnnouncementSelector;
